import { Alert, Box, Heading, Modal, Page, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';
import { SpinnerContainer } from '../SpinnerContainer';
import { SubmitRequestForm } from './SubmitRequestForm';
import { useEffect, useMemo, useState } from 'react';
import type { FetchGetHelpArticlesArgs, GetHelpArticlesResponse } from './actions';
import {
  fetchGetGuestHelpTopics,
  fetchGetHelpArticles,
  fetchGetHelpArticlesForLoggedInUsers,
  fetchGetHelpTopics,
} from './actions';
import { HelpArticlesContent } from './HelpArticles';
import type {
  GetHelpTopicListResponseV2Dto,
  HelpCategoryDto,
  HelpTopicV2Dto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { LoadingConstants, useRentalManagerUser } from '../..';
import { SubmitRequestModalPageFooter } from '.';
import { useLocation } from 'react-router-dom';
import { emitOpenSupportWidgetEvent, emitSubmitWidgetSupportRequestEvent } from './ClickstreamEvents';
import { css } from '@/styled-system/css';

export const footerStyles = css.raw({
  display: 'flex',
  justifyContent: { base: 'flex-end', lg: 'flex-start' },
  marginBlock: '600',
  lgDown: {
    borderBlock: '1px solid {colors.border.subtle}',
    position: 'fixed',
    width: '100%',
    bottom: 0,
    marginLeft: '{spacing.-400}',
    marginBottom: 0,
    padding: '400',
    background: 'bg.neutral',
    zIndex: `10000`,
  },
});

export type FormStateType = {
  category?: HelpCategoryDto;
  topic?: HelpTopicV2Dto;
  typedAddress: string;
  unit: string;
  description: string;
};

interface SubmitRequestModalPageProps {
  setCanSubmit: (val: boolean) => void;
  submitClicked: boolean;
  setSubmitClicked: (val: boolean) => void;
  setCategorySelected?: (val: boolean) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
  renderFooter?: () => JSX.Element;
}

/* Re-usable component that shows the submit request form and help articles side-by-side */
export const SubmitRequestPageComponent = ({
  setCanSubmit,
  submitClicked,
  setSubmitClicked,
  setCategorySelected,
  zrmwUser,
  queryObject,
  zrmSignInPage,
  renderFooter,
}: SubmitRequestModalPageProps) => {
  const [errorAlert, setErrorAlert] = useState('');
  const [requestState, setRequestState] = useState<LoadingConstants>(LoadingConstants.INITIAL);

  const [formState, setFormState] = useState<FormStateType>({
    category: undefined,
    topic: undefined,
    typedAddress: queryObject?.listingAddress || '',
    unit: queryObject?.unit || '',
    description: '',
  });

  const [helpTopics, setHelpTopics] = useState<GetHelpTopicListResponseV2Dto>();
  const [helpArticles, setHelpArticles] = useState<GetHelpArticlesResponse>();

  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;
  const { loggedIn } = user;
  const isUsingWidget = !renderFooter;

  const helpTopicsNotAvailable = helpTopics === undefined;
  const helpArticlesNotAvailable = helpArticles === undefined;

  useEffect(() => {
    if (loggedIn)
      fetchGetHelpTopics(setHelpTopics, () => setErrorAlert('An error has occurred. Please try again later.'));
    else fetchGetGuestHelpTopics(setHelpTopics, () => setErrorAlert('An error has occurred. Please try again later.'));

    emitOpenSupportWidgetEvent({ user });
  }, []);

  const showSpinner = useMemo(() => {
    return helpTopicsNotAvailable || helpArticlesNotAvailable || requestState === LoadingConstants.LOADING;
  }, [helpTopicsNotAvailable, helpArticlesNotAvailable, requestState]);

  useEffect(() => {
    const fetchGetHelpArticlesArgs: FetchGetHelpArticlesArgs = {
      params: { ...formState, category: formState?.category?.key, topic: formState?.topic?.key },
      onSuccess: setHelpArticles,
      onError: () => setErrorAlert(''),
    };

    if (loggedIn) {
      fetchGetHelpArticlesForLoggedInUsers(fetchGetHelpArticlesArgs);
    } else {
      fetchGetHelpArticles(fetchGetHelpArticlesArgs);
    }

    if (setCategorySelected) setCategorySelected(Boolean(formState.category));
  }, [formState?.category, formState?.topic, loggedIn, setCategorySelected]);

  const onSubmit = () => {
    if (isUsingWidget) {
      emitSubmitWidgetSupportRequestEvent({ user: zrmwUser || user });
    }
  };

  const renderSuccess = () => (
    <Alert tone="success" css={{ margin: '600' }}>
      <Text>Thank you! Your request has been submitted.</Text>
    </Alert>
  );

  const renderForm = () => {
    const formWidth = helpArticlesNotAvailable ? '100%' : '60%';
    return (
      <Box css={{ display: 'flex', justifyContent: 'space-between', xlDown: { flexDirection: 'column' } }}>
        <Box css={{ marginRight: '200' }} style={{ minWidth: formWidth }}>
          {!helpTopicsNotAvailable && !helpArticlesNotAvailable && (
            <SubmitRequestForm
              setCanSubmit={setCanSubmit}
              setRequestState={setRequestState}
              setFormState={setFormState}
              setErrorAlert={setErrorAlert}
              onSubmit={onSubmit}
              submitClicked={submitClicked}
              formState={formState}
              helpTopics={helpTopics}
              zrmwUser={zrmwUser}
              queryObject={queryObject}
              zrmSignInPage={zrmSignInPage}
            />
          )}
          {zrmwUser && renderFooter?.()}
        </Box>
        {!helpArticlesNotAvailable && <HelpArticlesContent data={helpArticles} error={false} />}
      </Box>
    );
  };

  const renderState = () => {
    switch (true) {
      case Boolean(requestState === LoadingConstants.ERROR):
        return (
          <Modal
            open={true}
            size={{ base: 'fullScreen', lg: 'md' }}
            header={<Heading level={1}>Unable to submit request</Heading>}
            body={<Text>{errorAlert}</Text>}
            onOpenChange={(open) => {
              if (!open) {
                setErrorAlert('');
                setSubmitClicked(false);
                setRequestState(LoadingConstants.INITIAL);
              }
            }}
          />
        );
      case Boolean(errorAlert):
        return (
          <Alert tone="critical">
            <Text>{errorAlert}</Text>
          </Alert>
        );
      case Boolean(requestState === LoadingConstants.SUCCESS):
        return renderSuccess();
      case Boolean(showSpinner):
        return <SpinnerContainer size="lg" height="600px" />;
      case Boolean(helpTopics?.categories?.length):
        return renderForm();
      default:
        return null;
    }
  };
  return <Box>{renderState()}</Box>;
};

export interface SubmitRequestPageProps {
  zrmwUser?: RentalManagerAuthCheckUserInfoWebApiDto;
  zrmSignInPage?: string;
}

/* Re-implementation of zrmw/SubmitRequestPage using SubmitRequestPageComponent */
export const SubmitRequestPage = ({ zrmwUser, zrmSignInPage }: SubmitRequestPageProps) => {
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [categorySelected, setCategorySelected] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const queryObject = {
    categoryKey: params.get('category'),
    topicKey: params.get('topic'),
    listingAddress: params.get('listingAddress'),
    unit: params.get('unit'),
    listingAlias: params.get('listingAlias'),
  };

  const afterSubmit = submitClicked && canSubmit;
  const showFooter = categorySelected && !afterSubmit;

  return (
    <>
      <Page.Root data-testid="SubmitRequestPage" css={{ backgroundColor: 'bg.neutral', xl: { minHeight: '900px' } }}>
        <Page.Breadcrumb>
          <TextButton asChild icon={<IconChevronLeftFilled />}>
            <a href="https://help.zillowrentalmanager.com/hc/en-us">Help center | Zillow Rental Manager</a>
          </TextButton>
        </Page.Breadcrumb>
        <Page.Header>
          <Heading level={1}>Submit a request</Heading>
        </Page.Header>
        <Page.Content>
          <SubmitRequestPageComponent
            setSubmitClicked={setSubmitClicked}
            submitClicked={submitClicked}
            setCanSubmit={setCanSubmit}
            setCategorySelected={setCategorySelected}
            zrmwUser={zrmwUser}
            queryObject={queryObject}
            zrmSignInPage={zrmSignInPage}
            renderFooter={
              showFooter
                ? () => (
                    <Box css={footerStyles}>
                      <SubmitRequestModalPageFooter
                        onClick={() => setSubmitClicked(true)}
                        disabled={!canSubmit}
                        customSubmitText={submitClicked ? 'Next' : undefined}
                      />
                    </Box>
                  )
                : undefined
            }
          />
        </Page.Content>
      </Page.Root>
    </>
  );
};
